import { Controller } from "@hotwired/stimulus"
import { showFlash } from "./flash_controller"

export default class extends Controller {
  static targets = ["buyerSelect", "sellerSelect", "addButton", "listContainer", "emptyState"]

  connect() {
    this.enableAddButton()

    // Listen for company selection events from the autocomplete controllers
    this.boundEnableAddButton = () => this.enableAddButton()
    document.addEventListener('company-selected', this.boundEnableAddButton)
    document.addEventListener('company-cleared', this.boundEnableAddButton)
  }

  disconnect() {
    document.removeEventListener('company-selected', this.boundEnableAddButton)
    document.removeEventListener('company-cleared', this.boundEnableAddButton)
  }

  // Enable/disable add button based on selection
  // Now requires at least one selection (buyer OR seller), not both
  enableAddButton() {
    const buyerValue = this.buyerSelectTarget.value
    const sellerValue = this.sellerSelectTarget.value

    const buyerSelected = buyerValue !== ""
    const sellerSelected = sellerValue !== ""

    // At least one must be selected
    const atLeastOneSelected = buyerSelected || sellerSelected

    // Both cannot be "all"
    const bothAreAll = buyerValue === "all" && sellerValue === "all"

    this.addButtonTarget.disabled = !atLeastOneSelected || bothAreAll
  }

  // Add a new buyer/seller pair or single-party rule to always need review
  async addPair() {
    const buyerId = this.buyerSelectTarget.value
    const sellerId = this.sellerSelectTarget.value

    // Validate: at least one must be selected
    if (!buyerId && !sellerId) {
      this.showError("Prašome pasirinkti bent vieną įmonę (pirkėją arba pardavėją)")
      return
    }

    // Validate: both cannot be "all"
    if (buyerId === "all" && sellerId === "all") {
      this.showError("Negalite pasirinkti 'Visi' abiems pusėms")
      return
    }

    // Check if this pair already exists
    if (this.pairAlreadyExists(buyerId, sellerId)) {
      this.showError("Ši taisyklė jau egzistuoja")
      return
    }

    // Get company names from the placeholder elements which show the selected company names
    const buyerPlaceholder = this.buyerSelectTarget.closest('[data-controller~="company-autocomplete"]').querySelector('[data-company-autocomplete-target="placeholder"]')
    const sellerPlaceholder = this.sellerSelectTarget.closest('[data-controller~="company-autocomplete"]').querySelector('[data-company-autocomplete-target="placeholder"]')

    const buyerName = buyerPlaceholder.textContent.trim()
    const sellerName = sellerPlaceholder.textContent.trim()

    try {
      const response = await fetch("/buyer_seller_settings/toggle", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          buyer_id: buyerId === "all" ? null : buyerId,
          seller_id: sellerId === "all" ? null : sellerId,
          requires_review: true
        })
      })

      const data = await response.json()

      if (data.success) {
        this.addPairToList(buyerId, sellerId, buyerName, sellerName)
        this.resetForm()
      } else {
        this.showError(data.error || "Klaida pridedant taisyklę")
      }
    } catch (error) {
      console.error("Error adding pair:", error)
      this.showError("Nepavyko pridėti taisyklės. Bandykite dar kartą.")
    }
  }

  // Remove a buyer/seller pair from always needing review
  async removePair(event) {
    const buyerId = event.target.dataset.buyerId
    const sellerId = event.target.dataset.sellerId
    const buyerName = event.target.dataset.buyerName
    const sellerName = event.target.dataset.sellerName

    // Simple direct removal like field values - no confirmation needed

    try {
      const response = await fetch("/buyer_seller_settings/toggle", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          buyer_id: buyerId === "all" ? null : buyerId,
          seller_id: sellerId === "all" ? null : sellerId,
          requires_review: false
        })
      })

      const data = await response.json()

      if (data.success) {
        this.removePairFromList(event.target)
      } else {
        this.showError(data.error || "Klaida šalinant taisyklę")
      }
    } catch (error) {
      console.error("Error removing pair:", error)
      this.showError("Nepavyko pašalinti taisyklės. Bandykite dar kartą.")
    }
  }

  // Add pair to the DOM list
  addPairToList(buyerId, sellerId, buyerName, sellerName) {
    const listContainer = this.listContainerTarget
    
    // Remove empty state if it exists
    const emptyState = listContainer.querySelector('[data-buyer-seller-settings-target="emptyState"]')
    if (emptyState) {
      emptyState.remove()
    }

    // Create or find the list
    let list = listContainer.querySelector('.space-y-2')
    if (!list) {
      list = document.createElement('div')
      list.className = 'space-y-2'
      listContainer.appendChild(list)
    }

    // Create new pair element
    const pairElement = document.createElement('div')
    pairElement.className = 'flex items-center justify-between rounded border border-gray-200 px-3 py-2'
    pairElement.innerHTML = `
      <div class="text-sm">
        <span class="font-medium text-gray-900">${sellerName}</span>
        <span class="text-gray-500">→</span>
        <span class="font-medium text-gray-900">${buyerName}</span>
      </div>
      <button type="button"
              class="text-sm text-gray-400 hover:text-red-600 transition-colors ml-2 cursor-pointer"
              data-action="buyer-seller-settings#removePair"
              data-buyer-id="${buyerId}"
              data-seller-id="${sellerId}"
              data-buyer-name="${buyerName}"
              data-seller-name="${sellerName}">
        ×
      </button>
    `

    list.appendChild(pairElement)
  }

  // Remove pair from the DOM list
  removePairFromList(buttonElement) {
    const pairElement = buttonElement.closest('.flex')
    pairElement.remove()

    // Check if list is now empty
    const list = this.listContainerTarget.querySelector('.space-y-2')
    if (list && list.children.length === 0) {
      list.remove()
      this.showEmptyState()
    }
  }

  // Show empty state
  showEmptyState() {
    const emptyStateHTML = `
      <div data-buyer-seller-settings-target="emptyState" class="rounded border border-dashed border-gray-300 px-3 py-8 text-center">
        <div class="flex flex-col items-center">
          <svg class="h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p class="text-sm text-gray-500">Nėra pažymėtų tiekėjų porų</p>
          <p class="text-xs text-gray-400 mt-1">Visos tiekėjų poros bus automatiškai apdorojamos</p>
        </div>
      </div>
    `
    this.listContainerTarget.innerHTML = emptyStateHTML
  }

  // Reset form after successful add
  resetForm() {
    // Clear hidden inputs
    this.buyerSelectTarget.value = ""
    this.sellerSelectTarget.value = ""
    
    // Reset the autocomplete placeholders to default state
    const buyerPlaceholder = this.buyerSelectTarget.closest('[data-controller~="company-autocomplete"]').querySelector('[data-company-autocomplete-target="placeholder"]')
    const sellerPlaceholder = this.sellerSelectTarget.closest('[data-controller~="company-autocomplete"]').querySelector('[data-company-autocomplete-target="placeholder"]')
    
    if (buyerPlaceholder) {
      buyerPlaceholder.textContent = "Pasirinkite pirkėją..."
      buyerPlaceholder.className = "text-gray-500"
    }
    
    if (sellerPlaceholder) {
      sellerPlaceholder.textContent = "Pasirinkite pardavėją..."
      sellerPlaceholder.className = "text-gray-500"
    }
    
    this.enableAddButton()
  }

  // Show success message
  showSuccess(message) {
    showFlash(message, 'success')
  }

  // Show error message
  showError(message) {
    showFlash(message, 'error')
  }

  // Check if a buyer/seller pair already exists in the list
  pairAlreadyExists(buyerId, sellerId) {
    const existingPairs = this.listContainerTarget.querySelectorAll('[data-buyer-id]')

    for (const pairElement of existingPairs) {
      const existingBuyerId = pairElement.dataset.buyerId
      const existingSellerId = pairElement.dataset.sellerId

      // Compare, treating "all" and empty as equivalent to null
      const normalizedBuyerId = (!buyerId || buyerId === "all") ? "all" : buyerId
      const normalizedSellerId = (!sellerId || sellerId === "all") ? "all" : sellerId
      const normalizedExistingBuyerId = (!existingBuyerId || existingBuyerId === "all") ? "all" : existingBuyerId
      const normalizedExistingSellerId = (!existingSellerId || existingSellerId === "all") ? "all" : existingSellerId

      if (normalizedExistingBuyerId === normalizedBuyerId && normalizedExistingSellerId === normalizedSellerId) {
        return true
      }
    }

    return false
  }
}
